/**
 * The fact library — the reason this is more than a planting toy.
 *
 * ## Why every card was rewritten
 *
 * This file used to hold figures about one particular park: its area, its
 * budget, its metro stations, its opening date. Every one of those was
 * sourced, and every one of them was a claim about a place this product no
 * longer has anything to do with. A claim about a named place cannot be
 * de-named — strip the name and it becomes either meaningless or a lie — so
 * the library is now about the plants in the garden, the way gardens work,
 * and the planet they all sit on.
 *
 * ## Do not add a card without a source
 *
 * The standard has not changed, only what it points at: a card must state
 * something a reader could check in a reference work, and it must be true
 * without a venue behind it. Where a figure would have to be hedged to be
 * honest, the card says the thing qualitatively instead and carries no
 * figure at all. **The cards below have not yet had a factual review under
 * the new standard** — that review is outstanding, and nothing here should
 * be treated as signed off because it is committed.
 *
 * Never invent, round or "improve" a number. If you cannot source a claim, do
 * not ship the claim.
 *
 * Content, not engine: a new project replaces this file. **Fact ids are stored
 * in saves (`factsSeen`) and referenced by `tiers.ts` and `weather.ts` — never
 * rename or reuse one.** Order matters too: cards are handed out top to bottom.
 */

export type FactCategory =
  | 'scale'
  | 'ecology'
  | 'culture'
  | 'engineering'
  | 'future';

export interface GardenFact {
  id: string;
  category: FactCategory;
  /** Big number or short phrase, rendered large. */
  headline: string;
  /** Supporting sentence. */
  body: string;
  /**
   * Optional illustration, relative to the site root.
   *
   * Three cards have one. They are generated plates, not photographs of
   * anywhere, so they illustrate rather than document — and none of them may
   * depict a specific place. Cards without one look exactly as they always
   * did.
   */
  image?: string;
}

export const CATEGORY_LABEL: Record<FactCategory, string> = {
  scale: 'Scale',
  ecology: 'Ecology',
  culture: 'Culture',
  engineering: 'Engineering',
  future: 'Future',
};

/**
 * Ordered from the broadest, most striking claims to the more specific ones,
 * because early cards are seen by everyone and late cards only by players who
 * stayed.
 */
export const FACTS: GardenFact[] = [
  {
    id: 'built-from-air',
    category: 'ecology',
    headline: 'Built out of air',
    body:
      'Almost all of a tree’s mass arrives through its leaves as carbon ' +
      'dioxide, not through its roots as soil.',
  },
  {
    id: 'trees-on-earth',
    category: 'scale',
    headline: '~3 trillion trees',
    body:
      'Earth carries somewhere around three trillion trees — and about half ' +
      'as many as it did before people began farming.',
  },
  {
    id: 'wood-is-carbon',
    category: 'ecology',
    headline: 'Half of wood is carbon',
    body:
      'About half the dry weight of a trunk is carbon the tree took out of ' +
      'the atmosphere. Wood is a record of the air it grew in.',
  },
  {
    id: 'wadi',
    image: './img/district-wadi.webp',
    category: 'ecology',
    headline: 'Cooler in the shade',
    body:
      'Air under an unbroken canopy is measurably cooler than air a step ' +
      'away in the sun. A tree does not only block light — it loses heat by ' +
      'evaporating water.',
  },
  {
    id: 'ghaf-roots',
    category: 'ecology',
    headline: '30 metres down',
    body:
      'Ghaf roots reach thirty metres for water. It shades the ground it ' +
      'stands on and enriches the soil beneath it.',
  },
  {
    id: 'charbagh',
    category: 'culture',
    headline: 'The Charbagh',
    body:
      'The fourfold garden: two water channels crossing at the centre, four ' +
      'quarters of planting, and the whole plan readable from the ground.',
  },
  {
    id: 'shaded-street',
    category: 'engineering',
    headline: 'The shaded street',
    body:
      'A street under a continuous canopy runs several degrees cooler than ' +
      'the same street without one. That is often the difference between ' +
      'walkable and not.',
  },
  {
    id: 'wetland',
    image: './img/district-wetland.webp',
    category: 'ecology',
    headline: 'Reed beds',
    body:
      'A shallow bed of reeds is a water treatment works with no moving ' +
      'parts. The plants, and the bacteria living on their roots, do all of ' +
      'the work.',
  },
  {
    id: 'roots-wider',
    category: 'ecology',
    headline: 'Wider than tall',
    body:
      'Most of a tree’s roots sit in the top metre of soil and spread ' +
      'further out than the branches above them.',
  },
  {
    id: 'water',
    category: 'engineering',
    headline: 'Used twice',
    body:
      'Water from a basin or a shower can irrigate a garden once it has been ' +
      'treated. In a dry place the cheapest new water is the water already ' +
      'there.',
  },
  {
    id: 'night-breathers',
    category: 'ecology',
    headline: 'Open after dark',
    body:
      'Some succulents only open their pores at night, taking in carbon ' +
      'dioxide in the cool and holding it until morning, to avoid losing ' +
      'water in the heat.',
  },
  {
    id: 'domes',
    image: './img/district-conservatory.webp',
    category: 'engineering',
    headline: 'Glass and lattice',
    body:
      'A geodesic shell carries its load through a web of short struts, ' +
      'which is how a glasshouse can be enormous and still be mostly sky.',
  },
  {
    id: 'thorns',
    category: 'ecology',
    headline: 'A thorn is a budget',
    body:
      'Thorns are cheap to grow and expensive to eat past, which is why so ' +
      'many trees of dry, grazed country carry them.',
  },
  {
    id: 'salt-leaves',
    category: 'ecology',
    headline: 'Salt through the leaf',
    body:
      'Tamarisk survives brackish ground by pushing salt out through glands ' +
      'on its own foliage. On a still day you can taste it on the leaves.',
  },
  {
    id: 'swollen-stem',
    category: 'ecology',
    headline: 'Water in the stem',
    body:
      'A desert rose keeps its reserve in a swollen trunk, which is how it ' +
      'can flower while everything around it is still waiting for rain.',
  },
  {
    id: 'date-palm-ancient',
    category: 'culture',
    headline: 'Tended for millennia',
    body:
      'The date palm is among the oldest cultivated trees. Some groves have ' +
      'been worked, in the same place, for thousands of years.',
  },
  {
    id: 'seeds-wait',
    category: 'future',
    headline: 'Seeds that wait',
    body:
      'Many dryland seeds can sit in dry ground for years and still ' +
      'germinate. A planting is as much a question of timing as of digging.',
  },
  {
    id: 'first-rain',
    category: 'engineering',
    headline: 'Catch the first storm',
    body:
      'In a dry climate most of the year’s rain falls in a handful of hours. ' +
      'A garden that holds that water keeps growing when none is falling.',
  },
  {
    id: 'pollinators',
    category: 'ecology',
    headline: 'Who the flower is for',
    body:
      'A flower is an advertisement aimed at something that moves. Plant for ' +
      'the pollinator and the fruit follows by itself.',
  },
  {
    id: 'canopy-line',
    category: 'scale',
    headline: 'Where the crowns meet',
    body:
      'Once neighbouring crowns touch, almost nothing below gets full sun. ' +
      'Most of a garden’s design is a decision about where that line falls.',
  },
  {
    id: 'old-trees',
    category: 'culture',
    headline: 'Older than the pyramids',
    body:
      'The oldest known living trees were already standing before the great ' +
      'pyramids were built, and they are standing now.',
  },
  {
    id: 'shade-for-later',
    category: 'future',
    headline: 'Shade for later',
    body:
      'A tree planted today gives its best shade to somebody who has not ' +
      'arrived yet. That is the whole argument for planting one.',
  },
];

export const TOTAL_FACTS = FACTS.length;

export function factById(id: string): GardenFact | null {
  return FACTS.find((f) => f.id === id) ?? null;
}

/**
 * The next fact the player has not seen, or null once the library is
 * exhausted. Facts are handed out in order rather than at fixed score
 * thresholds — the library is long enough now that pinning each card to a
 * tree count would strand most of it out of reach.
 */
export function nextFact(seen: ReadonlySet<string>): GardenFact | null {
  return FACTS.find((f) => !seen.has(f.id)) ?? null;
}
